import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDataContext } from "../contexts/AuthContext";
import {
  getAllCategories,
  getPrendasTutor,
  getAditionalDataOfItem,
  getSizes,
  completeSizes,
} from "../services/api";
import CargarTalle from "./CargarTalle.jsx";
import MediTuLittleModal from "./MediTuLittleModal.jsx";
import SizeTable from "./SizeTable";
import Modal1 from "./Modal1";
import Swal from "sweetalert2";
import logoPng from "../images/Frame.png";

/**
 * Sección de TALLES (independiente de Prendas). Muestra las prendas seleccionadas
 * con su cantidad y el avance de talles por prenda; permite modificar la selección
 * (solo si la tabla de talles NO fue completada por tutor/delegado ni aprobada por
 * el backoffice) y elegir para qué prenda cargar los talles. Al elegir una prenda
 * abre el formulario por unidad (CargarTalle), que ya trae la tabla de medidas + guía.
 */
const isBandera = (name) => String(name || "").trim().toLowerCase() === "bandera";

const C = {
  page: { padding: "1.1em 1em 2em", maxWidth: 760, margin: "0 auto" },
  h1: { margin: "0 0 .15em", fontFamily: '"Vinila Extended Bold", sans-serif', fontSize: "1.5em", color: "#0f172a" },
  sub: { margin: "0 0 1em", color: "#64748b", fontSize: ".95em" },
  modBtn: (disabled) => ({
    display: "inline-flex", alignItems: "center", gap: ".5em",
    border: "1.5px solid " + (disabled ? "#e2e8f0" : "#0071ce"),
    background: disabled ? "#f1f5f9" : "#fff",
    color: disabled ? "#94a3b8" : "#0071ce",
    borderRadius: ".7em", padding: ".55em .9em", fontWeight: 700, fontSize: ".9em",
    cursor: disabled ? "not-allowed" : "pointer",
  }),
  card: { display: "flex", alignItems: "center", gap: ".9em", background: "#fff", border: "1px solid #e9eef5", borderRadius: "1em", padding: ".9em 1em", marginBottom: ".7em", boxShadow: "0 1px 3px rgba(0,0,0,.06)" },
  cardName: { fontWeight: 800, color: "#0f172a", fontSize: "1em" },
  cardMeta: { color: "#64748b", fontSize: ".85em", marginTop: ".1em" },
  bar: { height: 6, borderRadius: 999, background: "#eef2f7", overflow: "hidden", marginTop: ".5em" },
  barFill: (pct, color) => ({ height: "100%", width: `${pct}%`, background: color, transition: "width .2s" }),
  cta: (disabled) => ({
    whiteSpace: "nowrap", border: "none", borderRadius: ".7em", padding: ".6em .9em",
    fontWeight: 700, fontSize: ".88em", cursor: disabled ? "default" : "pointer",
    background: disabled ? "#f1f5f9" : "#d54ab7", color: disabled ? "#94a3b8" : "#fff",
  }),
  badge: (bg, fg) => ({ display: "inline-block", background: bg, color: fg, borderRadius: 999, padding: ".15em .6em", fontSize: ".72em", fontWeight: 800, marginTop: ".15em" }),
  link: { background: "none", border: "none", color: "#0071ce", fontWeight: 700, fontSize: ".82em", cursor: "pointer", padding: 0, marginTop: ".35em" },
};

const statusOf = (it) => {
  if (it.approved) return { key: "aprobado", label: "Aprobado", bg: "#dcfce7", fg: "#15803d", barColor: "#22c55e" };
  if (it.completed) return { key: "confirmado", label: "Confirmado", bg: "#fef3c7", fg: "#b45309", barColor: "#f59e0b" };
  if (it.total > 0 && it.loaded >= it.total) return { key: "cargado", label: "Talles cargados", bg: "#fef3c7", fg: "#b45309", barColor: "#f59e0b" };
  return { key: "pendiente", label: "Pendiente", bg: "#fee2e2", fg: "#b91c1c", barColor: "#ef4444" };
};

export default function TallesSection() {
  const { contextData } = useDataContext();
  const { customer } = contextData;
  const navigate = useNavigate();
  // Tutor (3) = carga multi-alumno de todo el curso. Delegado (2) carga SOLO lo suyo,
  // pero puede gestionar al curso (confirmar talles + formulario/Excel de compañeros).
  const isTutor = customer.customer_type === 3;
  const canManageCourse = customer.customer_type === 2 || customer.customer_type === 3;

  const [loading, setLoading] = useState(true);
  const [prendas, setPrendas] = useState([]);      // lista con path/status
  const [seleccion, setSeleccion] = useState(null); // prenda elegida para cargar talle
  const [showMedi, setShowMedi] = useState(null);   // prenda para "¿cómo mido?"
  const [showModificar, setShowModificar] = useState(false);
  const [expandida, setExpandida] = useState(null); // order_item_id con la tabla abierta

  const fetchAll = useCallback(async () => {
    try {
      let base;
      if (isTutor) {
        base = await getPrendasTutor(
          customer.lead_id,
          customer.order_id,
          customer.access_code || customer.username
        );
      } else {
        base = await getAllCategories(customer.id, customer.order_id);
      }
      const lista = (Array.isArray(base) ? base : []).filter((p) => !isBandera(p.product_name));

      // Estado por prenda: cantidad, cargados, completado, aprobado, moldería.
      const conEstado = await Promise.all(
        lista.map(async (p) => {
          let total = Number(p.quantity || 0);
          let loaded = 0;
          let completed = false;
          let approved = false;
          // Moldería + tipo de prenda: de los campos del propio order_item (lo que
          // definió el backoffice). Son los que determinan los talles disponibles.
          let moldingId = p.molding_id || 0;
          let orderItemCategoryId = p.order_item_category_id || 0;
          try {
            const extra = await getAditionalDataOfItem(p.order_item_id);
            // Solo el tutor cuenta el total del curso (totalPaxes). El delegado/normal
            // cuentan SU cantidad (p.quantity).
            if (isTutor && Number(extra?.totalPaxes) > 0) total = Number(extra.totalPaxes);
            completed = !!extra?.completedMessage;
            approved = !!extra?.approvedMessage;
            // Fallback por si el order_item no trae la moldería en la lista.
            if (!moldingId) moldingId = extra?.moldingId || 0;
            if (!orderItemCategoryId) orderItemCategoryId = extra?.orderItemCategoryId || 0;
          } catch (e) { /* sin extra (p.ej. sin requirement), seguimos con la del item */ }
          try {
            const sizes = await getSizes(p.order_item_id);
            const mine = (Array.isArray(sizes) ? sizes : []).filter((s) =>
              isTutor ? true : s.customer_id === customer.id
            );
            loaded = mine.length;
          } catch (e) { /* sin talles cargados */ }
          return { ...p, total, loaded, completed, approved, moldingId, orderItemCategoryId };
        })
      );
      setPrendas(conEstado);
    } catch (error) {
      console.error("Error cargando talles:", error);
      setPrendas([]);
    } finally {
      setLoading(false);
    }
  }, [customer.id, customer.order_id, customer.lead_id, customer.access_code, customer.username, isTutor]);

  useEffect(() => {
    if (!customer?.id) return;
    fetchAll();
  }, [customer?.id, fetchAll]);

  // El bloqueo (no se puede modificar la selección) aplica si CUALQUIER prenda
  // ya fue completada por tutor/delegado o aprobada por el backoffice.
  const bloqueada = prendas.some((p) => p.completed || p.approved);

  const abrirCargar = (p) => setSeleccion(p);
  const cerrarCargar = () => { setSeleccion(null); fetchAll(); };

  // Antes de modificar la selección avisamos que puede generar costos extra + pendiente
  // (salvo que reduzca unidades). Requiere "Entendido" para continuar.
  const confirmarModificar = () => {
    Swal.fire({
      icon: "info",
      iconColor: "#0071ce",
      title: "Antes de cambiar tu selección",
      html:
        "Si <b>agregás</b> unidades puede generar <b>costos extra</b> a pagar y un " +
        "<b>pendiente</b> en tu cuenta. Si <b>reducís</b> unidades, no. " +
        "Tu plan de pagos se mantiene y se actualizan las cuotas.",
      confirmButtonText: "Entendido",
      confirmButtonColor: "#0071ce",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      cancelButtonColor: "#d54ab7",
    }).then((r) => {
      if (r.isConfirmed) setShowModificar(true);
    });
  };

  // Formulario público de talles (mismo que V3): params en base64. is_tutor=true → form
  // de carga masiva (delegado/tutor); false → form que se comparte a los compañeros.
  const formUrl = (tutorBool) =>
    `/form-sizes/${btoa(JSON.stringify(customer.order_id))}/${btoa(tutorBool ? "true" : "false")}`;

  const compartirFormulario = () => {
    const url = `${window.location.origin}${formUrl(false)}`;
    const fallback = () =>
      Swal.fire({
        icon: "info",
        title: "Compartí este link con tus compañeros",
        html: `<code style="word-break:break-all;font-size:.8em">${url}</code>`,
        confirmButtonColor: "#0071ce",
      });
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        Swal.fire({
          icon: "success",
          iconColor: "#22c55e",
          title: "Link copiado",
          html: `Compartilo con tus compañeros para que carguen su talle:<br/><br/><code style="word-break:break-all;font-size:.8em">${url}</code>`,
          confirmButtonColor: "#0071ce",
        });
      }).catch(fallback);
    } else {
      fallback();
    }
  };

  // Solo delegado/tutor: marcar la tabla de talles como COMPLETADA (la cierra; ya no
  // se puede cambiar talle/apodo/cantidades). Antes vivía en la sección Prendas.
  const confirmarTalles = (p) => {
    Swal.fire({
      icon: "question",
      iconColor: "#0071ce",
      title: `¿Confirmar los talles de ${p.product_name}?`,
      text: "Una vez confirmado no se podrán cambiar talles, apodos ni cantidades.",
      confirmButtonText: "Confirmar talles",
      confirmButtonColor: "#0071ce",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      cancelButtonColor: "#d54ab7",
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      try {
        await completeSizes({ order_item_id: p.order_item_id, customer_id: customer.id });
        fetchAll();
      } catch (e) {
        Swal.fire({ icon: "error", title: "Error", text: "No se pudieron confirmar los talles." });
      }
    });
  };

  if (loading) {
    return (
      <div className="contcargando">
        <img src={logoPng} alt="littleBlueLogo" className="imgcargando" />
        <h2 className="text-muted">CARGANDO...</h2>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div style={C.page}>
      <h1 style={C.h1}>Talles</h1>
      <p style={C.sub}>
        Estas son las prendas que elegiste. Cargá el talle de cada unidad. Cuando completes
        todas las unidades de una prenda, ese talle queda listo.
      </p>

      {/* Modificar selección (gateado por completado/aprobado) */}
      {customer.order_type !== 2 && (
        <div style={{ marginBottom: "1.1em" }}>
          <button
            type="button"
            style={C.modBtn(bloqueada)}
            disabled={bloqueada}
            onClick={() => !bloqueada && confirmarModificar()}
          >
            <i className="bi bi-pencil-square" /> Modificar selección de prendas
          </button>
          {bloqueada && (
            <p style={{ ...C.cardMeta, marginTop: ".4em" }}>
              No se puede modificar: la tabla de talles ya fue confirmada o aprobada.
            </p>
          )}
        </div>
      )}

      {/* Delegado y tutor: talles del resto del curso (formulario compartir / completar). */}
      {canManageCourse && customer.order_type !== 2 && (
        <div style={{ background: "#f8fafc", border: "1px solid #e9eef5", borderRadius: "1em", padding: "1em", marginBottom: "1.1em" }}>
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: ".2em" }}>
            Talles de mis compañeros
          </div>
          <p style={C.cardMeta}>
            Cargá los talles de todo el curso de una vez, o compartí el formulario para que
            cada uno cargue el suyo.
          </p>
          <div style={{ display: "flex", gap: ".6em", flexWrap: "wrap", marginTop: ".7em" }}>
            <button type="button" style={C.modBtn(false)} onClick={() => navigate(formUrl(true))}>
              <i className="bi bi-pencil-square" /> Completar formulario (carga masiva)
            </button>
            <button type="button" style={C.modBtn(false)} onClick={compartirFormulario}>
              <i className="bi bi-share" /> Compartir formulario
            </button>
          </div>
        </div>
      )}

      {prendas.length === 0 ? (
        <p style={C.sub}>No hay prendas con talles para cargar.</p>
      ) : (
        prendas.map((p) => {
          const st = statusOf(p);
          const pct = p.total > 0 ? Math.min(100, Math.round((p.loaded / p.total) * 100)) : 0;
          const bloqueado = p.completed || p.approved;        // confirmado o aprobado → no se edita
          const faltanCargar = p.total > 0 && p.loaded < p.total;
          const abierta = expandida === p.order_item_id;
          return (
            <div key={p.order_item_id}>
              <div style={C.card}>
                <i className="bi bi-rulers" style={{ fontSize: "1.6em", color: "#d54ab7", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={C.cardName}>
                    {p.product_name}{p.alias ? ` ${p.alias}` : ""}
                  </span>
                  <div style={C.cardMeta}>
                    {p.total} {p.total === 1 ? "unidad" : "unidades"} · {p.loaded}/{p.total} con talle
                  </div>
                  <div style={C.bar}><div style={C.barFill(pct, st.barColor)} /></div>
                  <span style={C.badge(st.bg, st.fg)}>{st.label}</span>
                  <div style={{ display: "flex", gap: "1em", flexWrap: "wrap" }}>
                    <button type="button" style={C.link} onClick={() => setShowMedi(p)}>
                      ¿Cómo mido el talle?
                    </button>
                    {p.loaded > 0 && (
                      <button
                        type="button"
                        style={C.link}
                        onClick={() => setExpandida(abierta ? null : p.order_item_id)}
                      >
                        {abierta ? "Ocultar talles cargados" : (bloqueado ? "Ver talles cargados" : "Ver / editar talles cargados")}
                      </button>
                    )}
                  </div>
                </div>
                {bloqueado ? (
                  <span style={C.cta(true)}>{st.label}</span>
                ) : faltanCargar ? (
                  <button type="button" style={C.cta(false)} onClick={() => abrirCargar(p)}>
                    Cargar talles
                  </button>
                ) : (
                  <button
                    type="button"
                    style={C.cta(false)}
                    onClick={() => setExpandida(abierta ? null : p.order_item_id)}
                  >
                    Editar talles
                  </button>
                )}
              </div>

              {/* Confirmar talles: solo delegado/tutor, cuando ya están todos cargados. */}
              {canManageCourse && !bloqueado && !faltanCargar && p.total > 0 && (
                <button
                  type="button"
                  onClick={() => confirmarTalles(p)}
                  style={{
                    width: "100%", margin: "-.3em 0 .9em", border: "1.5px solid #0071ce",
                    background: "#eef6ff", color: "#0071ce", borderRadius: ".8em",
                    padding: ".7em", fontWeight: 800, fontSize: ".9em", cursor: "pointer",
                  }}
                >
                  <i className="bi bi-check2-circle" /> Confirmar talles de {p.product_name}
                </button>
              )}

              {/* Tabla de talles cargados (editar/borrar habilitado si NO está bloqueado) */}
              {abierta && (
                <div style={{ margin: "-.3em 0 .9em", padding: "0 .2em" }}>
                  <SizeTable
                    orderItemId={p.order_item_id}
                    paxes={p.total}
                    botonHabilitado={!bloqueado}
                    onClose={cerrarCargar}
                    moldingId={p.moldingId}
                    orderItemCategoryId={p.orderItemCategoryId}
                  />
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Formulario de talles por unidad (reusa el flujo existente) */}
      {seleccion && (
        <CargarTalle
          onClose={cerrarCargar}
          orderItemId={seleccion.order_item_id}
          prenda={seleccion.product_name}
          prendasConPath={prendas}
          moldingId={seleccion.moldingId}
          orderItemCategoryId={seleccion.orderItemCategoryId}
        />
      )}

      {/* Instructivo "¿Cómo mido?" */}
      {showMedi && (
        <MediTuLittleModal
          prendaSeleccionada={showMedi}
          onClose={() => setShowMedi(null)}
          order_id={customer.order_id}
        />
      )}

      {/* Modificar la selección de prendas (mismo flujo que en el inicio) */}
      {showModificar && (
        <Modal1
          onNextClick={() => {
            setShowModificar(false);
            // Al cambiar la selección desde Talles, la compra vuelve a "pendiente":
            // el cliente debe re-confirmar su pedido + forma de pago (con el nuevo monto).
            try {
              localStorage.setItem(
                `mcb_compra_reconfirm_${customer.id}_${customer.order_id}`,
                "1"
              );
            } catch (e) { /* ignore */ }
            fetchAll();
          }}
          closeModal={() => setShowModificar(false)}
          setPaymentMethod={() => {}}
          paymentMethod={""}
          vengodelModal1={() => {}}
          vengodelModalPagos={false}
        />
      )}
    </div>
  );
}
